import React, { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

// ============================================================
// CONFIGURACIÓN
// ============================================================

const API_TURNOS =
  "https://wwwa.isepsantafe.net.ar/expedientes/obtenerTurnosPorDia";

// Si querés consultar solamente este grupo por defecto.
// Dejalo vacío "" si querés traer todos.
const GRUPO_POR_DEFECTO = "RECURSIVA_PSICOFISICA";

// ============================================================
// UTILIDADES
// ============================================================

const formatearFechaInput = (fecha) => {
  if (!fecha) return "";

  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, "0");
  const day = String(fecha.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatearFechaVisual = (fecha) => {
  if (!fecha) return "";

  try {
    const [year, month, day] = String(fecha)
      .substring(0, 10)
      .split("-");

    return `${day}/${month}/${year}`;
  } catch {
    return fecha;
  }
};

const generarRangoFechas = (desde, hasta) => {
  if (!desde || !hasta) return [];

  const fechas = [];

  const [yearDesde, monthDesde, dayDesde] = desde
    .split("-")
    .map(Number);

  const [yearHasta, monthHasta, dayHasta] = hasta
    .split("-")
    .map(Number);

  const actual = new Date(
    yearDesde,
    monthDesde - 1,
    dayDesde
  );

  const fin = new Date(
    yearHasta,
    monthHasta - 1,
    dayHasta
  );

  while (actual <= fin) {
    fechas.push(formatearFechaInput(actual));
    actual.setDate(actual.getDate() + 1);
  }

  return fechas;
};

const normalizar = (texto) =>
  String(texto || "")
    .toLowerCase()
    .trim();

// ============================================================
// COMPONENTE
// ============================================================

const TurnosRecursivas = ({
  procesoId,
  procesoNombre,
}) => {
  // ==========================================================
  // FECHAS
  // ==========================================================

  const hoy = formatearFechaInput(new Date());

  const [tipoConsulta, setTipoConsulta] = useState("dia");
  const [fecha, setFecha] = useState(hoy);
  const [fechaDesde, setFechaDesde] = useState(hoy);
  const [fechaHasta, setFechaHasta] = useState(hoy);

  // ==========================================================
  // FILTROS DE CONSULTA
  // ==========================================================

  const [grupoTurno, setGrupoTurno] = useState(
    GRUPO_POR_DEFECTO
  );

  const [estadoConsulta, setEstadoConsulta] = useState(
    "activo"
  );

  // ==========================================================
  // DATOS
  // ==========================================================

  const [turnos, setTurnos] = useState([]);
  const [respuestasPorDia, setRespuestasPorDia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [consultado, setConsultado] = useState(false);

  // ==========================================================
  // FILTROS LOCALES
  // ==========================================================

  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("");

  // ==========================================================
  // CONSULTAR UNA FECHA
  // ==========================================================

const consultarFecha = async (fechaConsulta) => {
  const body = {
    fecha: fechaConsulta,
    proceso_id: procesoId,
  };

  if (grupoTurno) {
    body.grupo_turno = grupoTurno;
  }

  if (estadoConsulta) {
    body.estado = estadoConsulta;
  }

  console.log("====================================");
  console.log("CONSULTANDO TURNOS");
  console.log("Proceso recibido:", procesoId);
  console.log("Nombre proceso:", procesoNombre);
  console.log("Fecha:", fechaConsulta);
  console.log("BODY ENVIADO:", body);
  console.log("====================================");

  const respuesta = await axios.post(
    API_TURNOS,
    body
  );

  console.log("RESPUESTA TURNOS:", respuesta.data);

  return {
    fechaConsulta,
    ...respuesta.data,
  };
};

  // ==========================================================
  // CONSULTAR
  // ==========================================================

  const consultar = async () => {
    try {
      if (!procesoId) {
        Swal.fire(
          "Atención",
          "No se pudo determinar el proceso seleccionado.",
          "warning"
        );
        return;
      }

      let fechas = [];

      if (tipoConsulta === "dia") {
        if (!fecha) {
          Swal.fire(
            "Atención",
            "Debe seleccionar una fecha.",
            "warning"
          );
          return;
        }

        fechas = [fecha];
      } else {
        if (!fechaDesde || !fechaHasta) {
          Swal.fire(
            "Atención",
            "Debe seleccionar fecha desde y fecha hasta.",
            "warning"
          );
          return;
        }

        if (fechaDesde > fechaHasta) {
          Swal.fire(
            "Atención",
            "La fecha desde no puede ser posterior a la fecha hasta.",
            "warning"
          );
          return;
        }

        fechas = generarRangoFechas(
          fechaDesde,
          fechaHasta
        );

        if (fechas.length > 60) {
          Swal.fire(
            "Rango demasiado grande",
            "Por el momento puede consultar hasta 60 días por vez.",
            "warning"
          );
          return;
        }
      }

      setLoading(true);
      setConsultado(false);
      setTurnos([]);
      setRespuestasPorDia([]);

      const resultados = [];

      // Se consulta día por día para no sobrecargar la API.
      for (const fechaConsulta of fechas) {
        try {
          const resultado = await consultarFecha(
            fechaConsulta
          );

          resultados.push(resultado);
        } catch (error) {
          console.error(
            `Error consultando ${fechaConsulta}:`,
            error
          );

          resultados.push({
            ok: false,
            fecha: fechaConsulta,
            fechaConsulta,
            cantidad: 0,
            turnos: [],
            error:
              error?.response?.data?.mensaje ||
              error?.response?.data?.error ||
              error.message,
          });
        }
      }

      // ======================================================
      // CONSOLIDAR RESULTADOS
      // ======================================================

      const todosLosTurnos = resultados.flatMap(
        (resultado) => {
          const lista = resultado?.turnos || [];

          return lista.map((turno) => ({
            ...turno,
            fecha_consulta:
              resultado.fechaConsulta ||
              resultado.fecha,
          }));
        }
      );

      setRespuestasPorDia(resultados);
      setTurnos(todosLosTurnos);
      setConsultado(true);
    } catch (error) {
      console.error(
        "Error consultando turnos:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.mensaje ||
          error?.response?.data?.error ||
          "No se pudieron consultar los turnos.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // FILTROS DISPONIBLES
  // ==========================================================

  const gruposDisponibles = useMemo(() => {
    return [
      ...new Set(
        turnos
          .map((turno) => turno.grupo_turno)
          .filter(Boolean)
      ),
    ].sort();
  }, [turnos]);

  const estadosDisponibles = useMemo(() => {
    return [
      ...new Set(
        turnos
          .map((turno) => turno.estado)
          .filter(Boolean)
      ),
    ].sort();
  }, [turnos]);

  const fechasDisponibles = useMemo(() => {
    return [
      ...new Set(
        turnos
          .map((turno) => turno.fecha_consulta)
          .filter(Boolean)
      ),
    ].sort();
  }, [turnos]);

  // ==========================================================
  // FILTRAR TABLA
  // ==========================================================

  const turnosFiltrados = useMemo(() => {
    const texto = normalizar(busqueda);

    return turnos.filter((turno) => {
      const coincideBusqueda =
        !texto ||
        [
          turno.dni,
          turno.nombre,
          turno.apellido,
          turno.nombre_completo,
          turno.email,
          turno.grupo_turno,
          turno.nombre_grupo,
          turno.estado,
          turno.turno_id,
        ].some((valor) =>
          normalizar(valor).includes(texto)
        );

      const coincideEstado =
        !filtroEstado ||
        normalizar(turno.estado) ===
          normalizar(filtroEstado);

      const coincideGrupo =
        !filtroGrupo ||
        turno.grupo_turno === filtroGrupo;

      const coincideFecha =
        !filtroFecha ||
        turno.fecha_consulta === filtroFecha;

      return (
        coincideBusqueda &&
        coincideEstado &&
        coincideGrupo &&
        coincideFecha
      );
    });
  }, [
    turnos,
    busqueda,
    filtroEstado,
    filtroGrupo,
    filtroFecha,
  ]);

  // ==========================================================
  // RESUMEN POR FECHA
  // ==========================================================

  const resumenPorFecha = useMemo(() => {
    const resumen = {};

    turnos.forEach((turno) => {
      const fechaTurno =
        turno.fecha_consulta ||
        String(turno.fecha || "").substring(0, 10);

      if (!resumen[fechaTurno]) {
        resumen[fechaTurno] = {
          fecha: fechaTurno,
          cantidad: 0,
          activos: 0,
          usados: 0,
          anulados: 0,
        };
      }

      resumen[fechaTurno].cantidad++;

      const estado = normalizar(turno.estado);

      if (estado === "activo") {
        resumen[fechaTurno].activos++;
      }

      if (estado === "usado") {
        resumen[fechaTurno].usados++;
      }

      if (estado === "anulado") {
        resumen[fechaTurno].anulados++;
      }
    });

    return Object.values(resumen).sort((a, b) =>
      a.fecha.localeCompare(b.fecha)
    );
  }, [turnos]);

  // ==========================================================
  // PERSONAS ÚNICAS
  // ==========================================================

  const personasUnicas = useMemo(() => {
    return new Set(
      turnos
        .map((turno) => turno.dni)
        .filter(Boolean)
        .map((dni) => String(dni))
    ).size;
  }, [turnos]);

  const personasFiltradas = useMemo(() => {
    return new Set(
      turnosFiltrados
        .map((turno) => turno.dni)
        .filter(Boolean)
        .map((dni) => String(dni))
    ).size;
  }, [turnosFiltrados]);

  // ==========================================================
  // LIMPIAR FILTROS
  // ==========================================================

  const limpiarFiltros = () => {
    setBusqueda("");
    setFiltroEstado("");
    setFiltroGrupo("");
    setFiltroFecha("");
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        padding: 20,
        maxWidth: 1500,
        margin: "0 auto",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      {/* ==================================================== */}
      {/* TÍTULO */}
      {/* ==================================================== */}

      <div
        style={{
          background:
            "linear-gradient(135deg, #0b2f5b, #174f86)",
          color: "white",
          padding: 22,
          borderRadius: 12,
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Administración de Turnos Recursivos
        </h2>

        <div
          style={{
            marginTop: 6,
            opacity: 0.9,
            fontSize: 14,
          }}
        >
          Consulta de turnos por día o rango de fechas
        </div>
      </div>

      {/* ==================================================== */}
      {/* PROCESO SELECCIONADO */}
      {/* ==================================================== */}

      <div
        style={{
          background: "#eef5ff",
          border: "1px solid #c9ddf5",
          borderRadius: 10,
          padding: "14px 18px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: "bold",
            color: "#58728d",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          Proceso seleccionado
        </div>

        <div
          style={{
            fontSize: 18,
            fontWeight: "bold",
            color: "#0b2f5b",
          }}
        >
          {procesoNombre ||
            `Proceso #${procesoId}`}
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            color: "#61758a",
          }}
        >
          ID del proceso:{" "}
          <strong>{procesoId || "-"}</strong>
        </div>
      </div>

      {/* ==================================================== */}
      {/* PANEL DE CONSULTA */}
      {/* ==================================================== */}

      <div style={styles.panel}>
        <h3
          style={{
            marginTop: 0,
            color: "#0b2f5b",
          }}
        >
          Consultar turnos
        </h3>

        {/* TIPO CONSULTA */}

        <div
          style={{
            display: "flex",
            gap: 15,
            marginBottom: 18,
            flexWrap: "wrap",
          }}
        >
          <label>
            <input
              type="radio"
              checked={tipoConsulta === "dia"}
              onChange={() =>
                setTipoConsulta("dia")
              }
            />{" "}
            Un día
          </label>

          <label>
            <input
              type="radio"
              checked={tipoConsulta === "rango"}
              onChange={() =>
                setTipoConsulta("rango")
              }
            />{" "}
            Rango de días
          </label>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 15,
            alignItems: "end",
          }}
        >
          {/* FECHA ÚNICA */}

          {tipoConsulta === "dia" && (
            <div>
              <label style={styles.label}>
                Fecha
              </label>

              <input
                type="date"
                value={fecha}
                onChange={(e) =>
                  setFecha(e.target.value)
                }
                style={styles.input}
              />
            </div>
          )}

          {/* RANGO */}

          {tipoConsulta === "rango" && (
            <>
              <div>
                <label style={styles.label}>
                  Desde
                </label>

                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) =>
                    setFechaDesde(e.target.value)
                  }
                  style={styles.input}
                />
              </div>

              <div>
                <label style={styles.label}>
                  Hasta
                </label>

                <input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) =>
                    setFechaHasta(e.target.value)
                  }
                  style={styles.input}
                />
              </div>
            </>
          )}

          {/* GRUPO */}

          <div>
            <label style={styles.label}>
              Grupo
            </label>

            <input
              type="text"
              value={grupoTurno}
              onChange={(e) =>
                setGrupoTurno(e.target.value)
              }
              placeholder="Todos"
              style={{
                ...styles.input,
                width: 230,
              }}
            />
          </div>

          {/* ESTADO */}

          <div>
            <label style={styles.label}>
              Estado
            </label>

            <select
              value={estadoConsulta}
              onChange={(e) =>
                setEstadoConsulta(e.target.value)
              }
              style={styles.input}
            >
              <option value="">
                Todos
              </option>

              <option value="activo">
                Activos
              </option>

              <option value="usado">
                Usados
              </option>

              <option value="anulado">
                Anulados
              </option>
            </select>
          </div>

          {/* BOTÓN */}

          <button
            onClick={consultar}
            disabled={loading || !procesoId}
            style={{
              ...styles.botonPrincipal,
              opacity:
                loading || !procesoId
                  ? 0.6
                  : 1,
            }}
          >
            {loading
              ? "Consultando..."
              : "🔎 Consultar"}
          </button>
        </div>
      </div>

      {/* ==================================================== */}
      {/* LOADING */}
      {/* ==================================================== */}

      {loading && (
        <div
          style={{
            padding: 30,
            textAlign: "center",
            fontSize: 18,
          }}
        >
          Consultando turnos...
        </div>
      )}

      {/* ==================================================== */}
      {/* RESULTADOS */}
      {/* ==================================================== */}

      {!loading && consultado && (
        <>
          {/* TARJETAS */}

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 15,
              marginBottom: 20,
            }}
          >
            <ResumenCard
              titulo="Turnos"
              valor={turnos.length}
              icono="📅"
            />

            <ResumenCard
              titulo="Personas"
              valor={personasUnicas}
              icono="👤"
            />

            <ResumenCard
              titulo="Días consultados"
              valor={respuestasPorDia.length}
              icono="📆"
            />

            <ResumenCard
              titulo="Resultados filtrados"
              valor={turnosFiltrados.length}
              icono="🔎"
            />
          </div>

          {/* ================================================= */}
          {/* RESUMEN POR DÍA */}
          {/* ================================================= */}

          {resumenPorFecha.length > 0 && (
            <div style={styles.panel}>
              <h3
                style={{
                  marginTop: 0,
                  color: "#0b2f5b",
                }}
              >
                Resumen por día
              </h3>

              <div
                style={{
                  overflowX: "auto",
                }}
              >
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>
                        Fecha
                      </th>
                      <th style={styles.th}>
                        Cantidad
                      </th>
                      <th style={styles.th}>
                        Activos
                      </th>
                      <th style={styles.th}>
                        Usados
                      </th>
                      <th style={styles.th}>
                        Anulados
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {resumenPorFecha.map((dia) => (
                      <tr key={dia.fecha}>
                        <td style={styles.td}>
                          <strong>
                            {formatearFechaVisual(
                              dia.fecha
                            )}
                          </strong>
                        </td>

                        <td style={styles.td}>
                          {dia.cantidad}
                        </td>

                        <td style={styles.td}>
                          {dia.activos}
                        </td>

                        <td style={styles.td}>
                          {dia.usados}
                        </td>

                        <td style={styles.td}>
                          {dia.anulados}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ================================================= */}
          {/* BUSCADOR */}
          {/* ================================================= */}

          <div style={styles.panel}>
            <h3
              style={{
                marginTop: 0,
                color: "#0b2f5b",
              }}
            >
              Buscar personas
            </h3>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "end",
              }}
            >
              <div
                style={{
                  flex: "1 1 300px",
                }}
              >
                <label style={styles.label}>
                  DNI, apellido, nombre, email...
                </label>

                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) =>
                    setBusqueda(e.target.value)
                  }
                  placeholder="Ej.: 30123456 o PEREYRA"
                  style={{
                    ...styles.input,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={styles.label}>
                  Fecha
                </label>

                <select
                  value={filtroFecha}
                  onChange={(e) =>
                    setFiltroFecha(
                      e.target.value
                    )
                  }
                  style={styles.input}
                >
                  <option value="">
                    Todas
                  </option>

                  {fechasDisponibles.map(
                    (fechaItem) => (
                      <option
                        key={fechaItem}
                        value={fechaItem}
                      >
                        {formatearFechaVisual(
                          fechaItem
                        )}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label style={styles.label}>
                  Grupo
                </label>

                <select
                  value={filtroGrupo}
                  onChange={(e) =>
                    setFiltroGrupo(
                      e.target.value
                    )
                  }
                  style={styles.input}
                >
                  <option value="">
                    Todos
                  </option>

                  {gruposDisponibles.map(
                    (grupo) => (
                      <option
                        key={grupo}
                        value={grupo}
                      >
                        {grupo}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label style={styles.label}>
                  Estado
                </label>

                <select
                  value={filtroEstado}
                  onChange={(e) =>
                    setFiltroEstado(
                      e.target.value
                    )
                  }
                  style={styles.input}
                >
                  <option value="">
                    Todos
                  </option>

                  {estadosDisponibles.map(
                    (estado) => (
                      <option
                        key={estado}
                        value={estado}
                      >
                        {estado}
                      </option>
                    )
                  )}
                </select>
              </div>

              <button
                onClick={limpiarFiltros}
                style={styles.botonSecundario}
              >
                Limpiar
              </button>
            </div>

            <div
              style={{
                marginTop: 12,
                fontSize: 14,
                color: "#555",
              }}
            >
              Mostrando{" "}
              <strong>
                {turnosFiltrados.length}
              </strong>{" "}
              turno(s) correspondientes a{" "}
              <strong>
                {personasFiltradas}
              </strong>{" "}
              persona(s).
            </div>
          </div>

          {/* ================================================= */}
          {/* TABLA */}
          {/* ================================================= */}

          <div style={styles.panel}>
            <h3
              style={{
                marginTop: 0,
                color: "#0b2f5b",
              }}
            >
              Detalle de turnos
            </h3>

            {turnosFiltrados.length === 0 ? (
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  color: "#666",
                }}
              >
                No se encontraron turnos.
              </div>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                }}
              >
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>
                        #
                      </th>
                      <th style={styles.th}>
                        Fecha
                      </th>
                      <th style={styles.th}>
                        Hora
                      </th>
                      <th style={styles.th}>
                        DNI
                      </th>
                      <th style={styles.th}>
                        Apellido
                      </th>
                      <th style={styles.th}>
                        Nombre
                      </th>
                      <th style={styles.th}>
                        Email
                      </th>
                      <th style={styles.th}>
                        Grupo
                      </th>
                      <th style={styles.th}>
                        Estado
                      </th>
                      <th style={styles.th}>
                        Turno ID
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {turnosFiltrados.map(
                      (turno, index) => (
                        <tr
                          key={`${turno.turno_id}-${turno.fecha_consulta}-${index}`}
                        >
                          <td style={styles.td}>
                            {index + 1}
                          </td>

                          <td style={styles.td}>
                            <strong>
                              {formatearFechaVisual(
                                turno.fecha_consulta
                              )}
                            </strong>
                          </td>

                          <td style={styles.td}>
                            {turno.hora ||
                              "09:00"}
                          </td>

                          <td style={styles.td}>
                            <strong>
                              {turno.dni}
                            </strong>
                          </td>

                          <td style={styles.td}>
                            {turno.apellido}
                          </td>

                          <td style={styles.td}>
                            {turno.nombre}
                          </td>

                          <td style={styles.td}>
                            {turno.email}
                          </td>

                          <td style={styles.td}>
                            {turno.nombre_grupo ||
                              turno.grupo_turno}
                          </td>

                          <td style={styles.td}>
                            <EstadoBadge
                              estado={
                                turno.estado
                              }
                            />
                          </td>

                          <td style={styles.td}>
                            {turno.turno_id}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// TARJETA RESUMEN
// ============================================================

const ResumenCard = ({
  titulo,
  valor,
  icono,
}) => {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #ddd",
        borderRadius: 10,
        padding: 18,
        boxShadow:
          "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          fontSize: 14,
          color: "#666",
        }}
      >
        {icono} {titulo}
      </div>

      <div
        style={{
          fontSize: 30,
          fontWeight: "bold",
          color: "#0b2f5b",
          marginTop: 5,
        }}
      >
        {valor}
      </div>
    </div>
  );
};

// ============================================================
// BADGE ESTADO
// ============================================================

const EstadoBadge = ({ estado }) => {
  const estadoNormalizado =
    normalizar(estado);

  let background = "#e5e7eb";
  let color = "#374151";

  if (estadoNormalizado === "activo") {
    background = "#dcfce7";
    color = "#166534";
  }

  if (estadoNormalizado === "usado") {
    background = "#dbeafe";
    color = "#1e40af";
  }

  if (estadoNormalizado === "anulado") {
    background = "#fee2e2";
    color = "#991b1b";
  }

  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 9px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: "bold",
        background,
        color,
        textTransform: "uppercase",
      }}
    >
      {estado || "Sin estado"}
    </span>
  );
};

// ============================================================
// ESTILOS
// ============================================================

const styles = {
  panel: {
    background: "#ffffff",
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
  },

  label: {
    display: "block",
    fontSize: 13,
    fontWeight: "bold",
    color: "#444",
    marginBottom: 5,
  },

  input: {
    padding: "9px 10px",
    border: "1px solid #ccc",
    borderRadius: 6,
    fontSize: 14,
    minHeight: 38,
    boxSizing: "border-box",
  },

  botonPrincipal: {
    background: "#0b2f5b",
    color: "#ffffff",
    border: "none",
    borderRadius: 6,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: "bold",
    cursor: "pointer",
    minHeight: 40,
  },

  botonSecundario: {
    background: "#6c757d",
    color: "#ffffff",
    border: "none",
    borderRadius: 6,
    padding: "10px 16px",
    cursor: "pointer",
    minHeight: 40,
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },

  th: {
    background: "#0b2f5b",
    color: "#ffffff",
    padding: "10px 8px",
    textAlign: "left",
    whiteSpace: "nowrap",
  },

  td: {
    borderBottom: "1px solid #e5e7eb",
    padding: "9px 8px",
    verticalAlign: "middle",
  },
};

export default TurnosRecursivas;